"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { useState } from "react";

import { format } from "date-fns";
import {
  Binoculars,
  Check,
  ChevronLeft,
  Glasses,
  Loader2,
  Telescope,
} from "lucide-react";
import { toast } from "sonner";

import EarlyCapyPrimaryIcon from "@/app/assets/vectors/early-capy-primary.svg";
import EarlyCapySecondaryIcon from "@/app/assets/vectors/early-capy-secondary.svg";
import { Button } from "@/frontend/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/frontend/components/ui/dialog";
import {
  useCreateBillingPortalSession,
  useCreateSubscriptionCheckoutSession,
  useDowngradeSubscription,
  usePlanQuery,
  useStripeSubscriptionQuery,
  useUpgradeSubscription,
} from "@/frontend/hooks/subscription";
import { getErrorMessage } from "@/frontend/lib/error-utils";
import { PLANS } from "@/frontend/lib/plans";
import { useSidebarStore } from "@/frontend/lib/stores";
import { cn } from "@/frontend/lib/utils";
import { Plan } from "@/shared/types";

// Helper function to determine plan relationship
function getPlanRelationship(
  currentPlan: Plan,
  targetPlan: Plan,
): "current" | "upgrade" | "downgrade" {
  const planHierarchy: Record<Plan, number> = {
    free: 0,
    lite: 1,
    pro: 2,
  };

  if (currentPlan === targetPlan) return "current";
  if (planHierarchy[targetPlan] > planHierarchy[currentPlan]) return "upgrade";
  return "downgrade";
}

type ViewMode = "plans" | "upgrade" | "downgrade";

export function UpgradeDialog() {
  const router = useRouter();
  const { isUpgradeDialogOpen, setIsUpgradeDialogOpen } = useSidebarStore();
  const [viewMode, setViewMode] = useState<ViewMode>("plans");

  const planQuery = usePlanQuery();
  const stripeSubscriptionQuery = useStripeSubscriptionQuery();

  const currentPlan = planQuery.data ?? "free";
  const hasActiveSubscription =
    stripeSubscriptionQuery.data?.subscriptionId !== null;

  const handleOpenChange = (open: boolean) => {
    setIsUpgradeDialogOpen(open);
    if (!open) {
      setViewMode("plans");
    }
  };

  const createSubscriptionCheckoutSessionMutation =
    useCreateSubscriptionCheckoutSession();
  const createBillingPortalSessionMutation = useCreateBillingPortalSession();
  const upgradeSubscriptionMutation = useUpgradeSubscription();
  const downgradeSubscriptionMutation = useDowngradeSubscription();

  const upgradePlan = PLANS.pro;
  const downgradePlan = PLANS.lite;
  const currentPlanDetails = PLANS[currentPlan];

  const handleUpgrade = () => {
    upgradeSubscriptionMutation.mutate(
      { targetPlan: "pro" },
      {
        onSuccess: (url) => {
          toast.success("Subscription upgraded successfully!");
          if (url) {
            router.push(url);
          }
          setViewMode("plans");
          setIsUpgradeDialogOpen(false);
        },
        onError: (error) => {
          toast.error(getErrorMessage(error, "Failed to upgrade subscription"));
        },
      },
    );
  };

  const handleDowngrade = () => {
    downgradeSubscriptionMutation.mutate(
      { targetPlan: "lite" },
      {
        onSuccess: (url) => {
          toast.success(
            `Downgrade scheduled for the end of your billing cycle ${
              stripeSubscriptionQuery.data?.currentPeriodEnd &&
              ` (${format(
                new Date(stripeSubscriptionQuery.data.currentPeriodEnd),
                "MMM d",
              )})`
            }`,
          );
          if (url) {
            router.push(url);
          }
          setViewMode("plans");
          setIsUpgradeDialogOpen(false);
        },
        onError: (error) => {
          toast.error(getErrorMessage(error, "Failed to schedule downgrade"));
        },
      },
    );
  };

  return (
    <Dialog open={isUpgradeDialogOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        fullscreen
        className="flex flex-col items-center justify-center gap-4 rounded-none sm:max-w-full md:gap-16"
      >
        {viewMode === "plans" ? (
          // Plan selection view
          <>
            <DialogHeader className="gap-4">
              <DialogTitle className="text-center text-xl font-semibold tracking-tight md:text-4xl">
                Upgrade to Scout more!
              </DialogTitle>
              <DialogDescription className="text-center text-sm text-muted md:text-base">
                Cook harder with a plan that best suits you.{" "}
                <Link
                  href="/faq"
                  className="text-primary hover:underline"
                  onClick={() => {
                    setIsUpgradeDialogOpen(false);
                  }}
                >
                  Learn more.
                </Link>
              </DialogDescription>
            </DialogHeader>
            <div
              className="flex w-full max-w-6xl flex-col space-y-4 overflow-y-auto md:flex-row md:space-y-0 md:space-x-4"
              style={{
                scrollbarWidth: "none",
              }}
            >
              {Object.values(PLANS).map((plan) => (
                <div
                  key={plan.id}
                  className={cn(
                    "flex w-full flex-col gap-4 rounded-3xl border bg-input p-4 text-foreground standalone:h-full",
                  )}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center justify-center rounded-sm bg-white p-1 shadow-xs">
                      <div
                        className={cn(
                          "rounded-xs p-1.5",
                          plan.id === "free" && "bg-input",
                          plan.id === "lite" && "bg-secondary/10",
                          plan.id === "pro" && "bg-primary/10",
                        )}
                      >
                        {plan.id === "free" && (
                          <Glasses
                            size={20}
                            className="text-muted-foreground"
                          />
                        )}
                        {plan.id === "lite" && (
                          <Binoculars size={20} className="text-secondary" />
                        )}
                        {plan.id === "pro" && (
                          <Telescope size={20} className="text-primary" />
                        )}
                      </div>
                    </div>
                    {plan.id === "lite" && (
                      <Image
                        src={EarlyCapySecondaryIcon}
                        alt="Early capy price"
                        width={128}
                      />
                    )}
                    {plan.id === "pro" && (
                      <Image
                        src={EarlyCapyPrimaryIcon}
                        alt="Early capy price"
                        width={128}
                      />
                    )}
                  </div>
                  <div className="flex flex-col text-left">
                    <h3 className="text-lg font-semibold">
                      {PLANS[plan.id as Plan].fullName}
                    </h3>
                    <div className="h-5 text-sm text-muted-foreground">
                      {plan.id !== "free" && "Billed monthly"}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-3xl font-semibold">
                        ${plan.earlyPrice}
                      </span>
                      <span className="ml-1 text-sm font-medium text-muted-foreground">
                        {plan.id !== "free" && (
                          <span className="line-through">{plan.price}</span>
                        )}{" "}
                        / mo<span className="hidden lg:inline">nth</span>
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="default"
                    className="my-2 bg-foreground hover:bg-foreground/90"
                    disabled={
                      getPlanRelationship(currentPlan, plan.id as Plan) ===
                      "current"
                    }
                    onClick={() => {
                      const relationship = getPlanRelationship(
                        currentPlan,
                        plan.id as Plan,
                      );

                      // Handle free plan when subscribed to a paid plan
                      if (
                        plan.id === "free" &&
                        hasActiveSubscription &&
                        currentPlan !== "free"
                      ) {
                        createBillingPortalSessionMutation.mutate(undefined, {
                          onSuccess: (url) => {
                            if (url) {
                              router.push(url);
                            }
                          },
                          onError: (error) => {
                            toast.error(
                              getErrorMessage(
                                error,
                                "Failed to create billing portal session",
                              ),
                            );
                          },
                        });
                        return;
                      }

                      // Handle upgrades
                      if (relationship === "upgrade") {
                        if (
                          currentPlan === "lite" &&
                          plan.id === "pro" &&
                          hasActiveSubscription
                        ) {
                          setViewMode("upgrade");
                        } else {
                          // Regular Stripe checkout for free -> lite/pro
                          createSubscriptionCheckoutSessionMutation.mutate(
                            { plan: plan.id as Plan },
                            {
                              onSuccess: (url) => {
                                if (url) {
                                  router.push(url);
                                }
                              },
                              onError: (error) => {
                                toast.error(
                                  getErrorMessage(
                                    error,
                                    "Failed to create checkout session",
                                  ),
                                );
                              },
                            },
                          );
                        }
                      }

                      // Handle downgrades
                      if (relationship === "downgrade") {
                        if (currentPlan === "pro" && plan.id === "lite") {
                          setViewMode("downgrade");
                        }
                      }
                    }}
                  >
                    {(() => {
                      const relationship = getPlanRelationship(
                        currentPlan,
                        plan.id as Plan,
                      );

                      if (
                        plan.id === "free" &&
                        hasActiveSubscription &&
                        currentPlan !== "free"
                      ) {
                        return "Manage";
                      }

                      if (relationship === "current") {
                        return "Current plan";
                      }

                      if (relationship === "upgrade") {
                        return `Upgrade to ${PLANS[plan.id as Plan].name}`;
                      }

                      if (relationship === "downgrade") {
                        return `Downgrade to ${PLANS[plan.id as Plan].name}`;
                      }
                    })()}
                  </Button>
                  <ul className="space-y-4 rounded-xl bg-white p-4">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check
                          className="mt-0.5 flex-shrink-0 text-muted-foreground"
                          size={16}
                        />
                        <div className="text-start text-sm text-foreground/90">
                          {feature}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-muted-foreground">
              Redeem the Early Capy discount (50% off for 3 months)
            </p>
          </>
        ) : viewMode === "upgrade" ? (
          // Upgrade from Lite to Pro view
          <>
            <div
              className="absolute top-4 left-4 cursor-pointer rounded-full bg-muted-foreground/20 p-1.5 ring-offset-background transition-all hover:bg-muted-foreground/10 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
              onClick={() => setViewMode("plans")}
            >
              <ChevronLeft className="text-white" />
              <span className="sr-only">Back</span>
            </div>
            <DialogHeader className="gap-4">
              <DialogTitle className="text-center text-xl font-semibold tracking-tight md:text-4xl">
                Upgrade to Scout Pro
              </DialogTitle>
              <DialogDescription className="text-center text-sm text-muted md:text-base">
                Unlock 5× more agent hours with Pro!{" "}
                <Link
                  href="/faq"
                  className="text-primary hover:underline"
                  onClick={() => {
                    setIsUpgradeDialogOpen(false);
                  }}
                >
                  Learn more.
                </Link>
              </DialogDescription>
            </DialogHeader>

            <div className="flex w-full max-w-lg flex-col">
              <div className="flex h-full w-full flex-col gap-4 rounded-3xl border bg-input p-4 text-foreground">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center justify-center rounded-sm bg-white p-1 shadow-xs">
                    <div className="rounded-xs bg-primary/10 p-1.5">
                      <Telescope size={20} className="text-primary" />
                    </div>
                  </div>
                  <Image
                    src={EarlyCapyPrimaryIcon}
                    alt="Early capy price"
                    width={128}
                  />
                </div>
                <div className="flex flex-col text-left">
                  <h3 className="text-lg font-semibold">Scout Pro</h3>
                  <div className="h-5 text-sm text-muted-foreground">
                    Everything in Lite, plus:
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-3xl font-semibold">$99</span>
                    <span className="ml-1 text-sm font-medium text-muted-foreground">
                      <span className="line-through">$199</span> / month
                    </span>
                  </div>
                </div>
                <ul className="h-full space-y-4 rounded-xl bg-white p-4">
                  {upgradePlan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check
                        className="mt-0.5 flex-shrink-0 text-muted-foreground"
                        size={16}
                      />
                      <div className="text-sm text-foreground/90">
                        {feature}
                        {feature.toLowerCase().includes("agent hour") && (
                          <span className="ml-1 font-semibold text-primary">
                            (5× more!)
                          </span>
                        )}
                        {feature.toLowerCase().includes("concurrent jam") && (
                          <span className="ml-1 font-semibold text-primary">
                            (3 more!)
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Due today (prorated)
                  </span>
                  <span className="text-lg font-semibold">
                    $
                    {(upgradePlan?.earlyPrice ?? 0) -
                      (currentPlanDetails?.earlyPrice ?? 0)}
                  </span>
                </div>
                <Button
                  variant="default"
                  className="w-full rounded-xl bg-foreground hover:bg-foreground/90"
                  onClick={handleUpgrade}
                  disabled={upgradeSubscriptionMutation.isPending}
                >
                  {upgradeSubscriptionMutation.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    "Upgrade to Pro"
                  )}
                </Button>
              </div>
            </div>
          </>
        ) : (
          // Downgrade from Pro to Lite view
          <>
            <div
              className="absolute top-4 left-4 cursor-pointer rounded-full bg-muted-foreground/20 p-1.5 ring-offset-background transition-all hover:bg-muted-foreground/10 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
              onClick={() => setViewMode("plans")}
            >
              <ChevronLeft className="text-white" />
              <span className="sr-only">Back</span>
            </div>
            <DialogHeader className="gap-4">
              <DialogTitle className="text-center text-xl font-semibold tracking-tight md:text-4xl">
                Switch to Scout Lite
              </DialogTitle>
              <DialogDescription className="text-center text-sm text-muted md:text-base">
                Scout with less.{" "}
                <Link
                  href="/faq"
                  className="text-primary hover:underline"
                  onClick={() => {
                    setIsUpgradeDialogOpen(false);
                  }}
                >
                  Learn more.
                </Link>
              </DialogDescription>
            </DialogHeader>

            <div className="flex w-full max-w-lg flex-col">
              <div className="flex h-full w-full flex-col gap-4 rounded-3xl border bg-input p-4 text-foreground">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center justify-center rounded-sm bg-white p-1 shadow-xs">
                    <div className="rounded-xs bg-secondary/10 p-1.5">
                      <Binoculars size={20} className="text-secondary" />
                    </div>
                  </div>
                  <Image
                    src={EarlyCapySecondaryIcon}
                    alt="Early capy price"
                    width={128}
                  />
                </div>
                <div className="flex flex-col text-left">
                  <h3 className="text-lg font-semibold">Scout Lite</h3>
                  <div className="h-5 text-sm text-muted-foreground">
                    You&apos;ll be switching to:
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-3xl font-semibold">$19</span>
                    <span className="ml-1 text-sm font-medium text-muted-foreground">
                      <span className="line-through">$39</span> / month
                    </span>
                  </div>
                </div>
                <ul className="h-full space-y-4 rounded-xl bg-white p-4">
                  {downgradePlan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check
                        className="mt-0.5 flex-shrink-0 text-muted-foreground"
                        size={16}
                      />
                      <div className="text-sm text-foreground/90">
                        {feature}
                        {feature.toLowerCase().includes("agent hour") && (
                          <span className="ml-1 font-semibold text-amber-600 dark:text-amber-500">
                            (5× less)
                          </span>
                        )}
                        {feature.toLowerCase().includes("concurrent jam") && (
                          <span className="ml-1 font-semibold text-amber-600 dark:text-amber-500">
                            (3 less)
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="rounded-xl bg-amber-500/10 p-4 text-center text-sm text-amber-600 dark:text-amber-500">
                  You&apos;ll lose access to 24 extra agent hours and beta
                  features at the end of your billing cycle
                  {stripeSubscriptionQuery.data?.currentPeriodEnd &&
                    ` (${format(
                      new Date(stripeSubscriptionQuery.data.currentPeriodEnd),
                      "MMM d",
                    )})`}
                  . You can upgrade again later.
                </div>

                <Button
                  variant="default"
                  className="w-full rounded-xl bg-foreground hover:bg-foreground/90"
                  onClick={handleDowngrade}
                  disabled={downgradeSubscriptionMutation.isPending}
                >
                  {downgradeSubscriptionMutation.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    "Downgrade to Lite"
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
